/**
 * SiteAnalysisTab — voltide_site_analysis_phase2_vault
 *
 * Streamlined flow inside ProspectSiteDetailPage:
 *   1. Data sources — pulled from the Requirement tab's Document Vault
 *      (electricity bills + DG logs). User just toggles which to include.
 *   2. AI extracts each selected bill via Claude vision → user reviews
 *      / corrects the spreadsheet table.
 *   3. Confirm → analytics + narrative + industry context → branded PDF.
 *
 * Context (industry, grid reliability, has_dg, has_solar, backup hours, etc.)
 * is auto-populated from the Requirement record. The SiteContextEditor only
 * surfaces the two new advisor-asserted fields: industry_type and
 * grid_reliability_rating.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileText, Loader2, CheckCircle, AlertTriangle, Sparkles,
  Download, RefreshCw, Plus, Trash2, FlaskConical, Zap, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { siteAnalysisAPI } from '../utils/api';
import { formatINR, formatKWh } from '../utils/format';
import {
  sortBillsByMonth, isBlankBill, recomputeHeadlineInsights,
  computeBillTotals, applyQuickFix, formatINRIndian,
} from '../utils/billAnalytics';

const STAGES = [
  { id: 'sources', label: 'Data sources', icon: FileText },
  { id: 'review', label: 'Review extracted', icon: Sparkles },
  { id: 'report', label: 'Report', icon: FlaskConical },
];

const GRID_OPTIONS = ['Stable', 'Moderately Unstable', 'Highly Unstable'];

function SiteContextEditor({ analysis, onUpdate }) {
  const ctx = analysis.site_ctx || {};
  const [form, setForm] = useState({
    client_website_url: ctx.client_website_url || '',
    industry_brief: ctx.industry_brief || '',
    industry_category: ctx.industry_category || '',
    energy_criticality: ctx.energy_criticality || '',
    primary_bess_driver: ctx.primary_bess_driver || '',
    industry_fallback_text: ctx.industry_fallback_text || '',
    grid_reliability_rating: ctx.grid_reliability_rating || '',
  });
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [scrapeSummary, setScrapeSummary] = useState(null);

  const handleField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAnalyseSite = async () => {
    setDetecting(true);
    try {
      const payload = form.client_website_url
        ? { website_url: form.client_website_url }
        : { fallback_text: form.industry_fallback_text };
      const r = await siteAnalysisAPI.detectIndustry(analysis.analysis_id, payload);
      setForm(f => ({
        ...f,
        client_website_url: r.data.client_website_url || f.client_website_url,
        industry_brief: r.data.industry_brief || f.industry_brief,
        industry_category: r.data.industry_category || '',
        energy_criticality: r.data.energy_criticality || '',
        primary_bess_driver: r.data.primary_bess_driver || '',
      }));
      setScrapeSummary(r.data.scrape_summary || null);
      const src = r.data.scrape_summary?.source;
      if (src === 'website') {
        toast.success(`Analysed ${r.data.scrape_summary.pages_fetched} page(s) — review and edit the brief below`);
      } else if (src === 'fallback_text') {
        toast.success('Brief generated from your description');
      } else {
        toast.warning('Could not reach the website — using a generic placeholder. Edit the brief manually.');
      }
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Industry detection failed');
    } finally {
      setDetecting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await siteAnalysisAPI.updateSiteCtx(analysis.analysis_id, form);
      onUpdate(r.data);
      toast.success('Advisor context saved');
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const criticalityTone = form.energy_criticality === 'high'
    ? 'bg-red-50 text-red-700 border-red-200'
    : form.energy_criticality === 'low'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="site-context-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#0061DD]"/>1. Advisor context (drives Section 3)
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Paste the customer's website — AI scrapes it and writes a tailored industry brief.
            Edit before saving. Brief feeds the Energy Advisory Report's Section 3.
          </p>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving} variant="outline" data-testid="save-site-ctx">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : null}Save context
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mb-3">
        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider">Client website URL</label>
          <div className="flex gap-2 mt-0.5">
            <Input
              className="h-8 text-xs flex-1"
              placeholder="https://www.technocraftfashions.com"
              value={form.client_website_url}
              onChange={(e) => handleField('client_website_url', e.target.value)}
              data-testid="client-website-url"
            />
            <Button
              size="sm"
              type="button"
              onClick={handleAnalyseSite}
              disabled={detecting || (!form.client_website_url && !form.industry_fallback_text)}
              className="bg-[#0061DD] hover:bg-[#0050B5] whitespace-nowrap"
              data-testid="analyse-site-btn"
            >
              {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5 mr-1.5"/>}
              Analyse Site
            </Button>
          </div>
          {!form.client_website_url && (
            <button
              type="button"
              onClick={() => setShowFallback(s => !s)}
              className="text-[10px] text-[#0061DD] hover:underline mt-1"
              data-testid="toggle-fallback-text"
            >
              No website URL — describe the business manually instead
            </button>
          )}
          {showFallback && !form.client_website_url && (
            <textarea
              className="mt-1.5 w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs"
              rows={3}
              placeholder="Describe the client's business in 2-3 sentences — what they make/do, shift pattern, critical loads."
              value={form.industry_fallback_text}
              onChange={(e) => handleField('industry_fallback_text', e.target.value)}
              data-testid="industry-fallback-text"
            />
          )}
        </div>

        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider">Grid reliability (after reviewing the data)</label>
          <select
            value={form.grid_reliability_rating}
            onChange={(e) => handleField('grid_reliability_rating', e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs mt-0.5 bg-white"
            data-testid="grid-reliability"
          >
            <option value="">— select —</option>
            {GRID_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      {/* Brief output — editable */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] text-slate-500 uppercase tracking-wider">AI-detected industry brief</label>
          <div className="flex items-center gap-2">
            {form.industry_category && (
              <span className="text-[10px] px-2 py-0.5 bg-[#0061DD]/10 text-[#0061DD] rounded-sm font-semibold" data-testid="industry-category-badge">
                {form.industry_category}
              </span>
            )}
            {form.energy_criticality && (
              <span className={`text-[10px] px-2 py-0.5 rounded-sm font-semibold border ${criticalityTone}`} data-testid="energy-criticality-badge">
                {form.energy_criticality} criticality
              </span>
            )}
          </div>
        </div>
        <textarea
          rows={6}
          className="w-full px-3 py-2 border border-slate-200 rounded-sm text-xs leading-relaxed focus:border-[#0061DD] focus:outline-none"
          placeholder='Click "Analyse Site" above to generate a tailored brief, or type your own.'
          value={form.industry_brief}
          onChange={(e) => handleField('industry_brief', e.target.value)}
          data-testid="industry-brief"
        />
        {form.primary_bess_driver && (
          <div className="mt-1.5 text-xs bg-blue-50/30 border border-blue-100 rounded-sm px-2 py-1.5" data-testid="primary-bess-driver">
            <span className="text-[10px] uppercase tracking-wider text-[#0061DD] font-semibold mr-1.5">Primary BESS driver —</span>
            {form.primary_bess_driver}
          </div>
        )}
        {scrapeSummary?.urls_fetched?.length > 0 && (
          <p className="text-[10px] text-slate-400 mt-1" data-testid="scrape-summary">
            Sourced from {scrapeSummary.pages_fetched} page(s): {scrapeSummary.urls_fetched.join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}

function StageStepper({ activeStage, analysis }) {
  const stageOrder = ['upload', 'review', 'report'];
  const currentIdx = stageOrder.indexOf(activeStage);
  return (
    <div className="flex items-center bg-white border border-slate-200 rounded-sm p-1.5 mb-4 gap-1" data-testid="analysis-stage-stepper">
      {STAGES.map((s, i) => {
        const Icon = s.icon;
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <React.Fragment key={s.id}>
            {i > 0 && <div className="w-4 h-px bg-slate-300 shrink-0"/>}
            <div className={`flex items-center space-x-2 px-3 py-2 rounded-sm text-xs font-medium flex-1 ${
              active ? 'bg-[#0061DD] text-white' : done ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-500 border border-slate-200'
            }`}>
              <Icon className="w-3.5 h-3.5"/>
              <span>{s.label}</span>
              {done && <CheckCircle className="w-3 h-3 ml-auto"/>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const FIELDS = [
  { key: 'bill_month', label: 'Month', width: 'w-24', type: 'text', frozen: true },
  { key: 'units_kwh', label: 'Units (kWh)', width: 'w-24', type: 'number', dp: 0 },
  { key: 'max_demand_kw', label: 'Max kW', width: 'w-20', type: 'number', step: '0.01', dp: 2 },
  { key: 'power_factor', label: 'PF', width: 'w-16', type: 'number', step: '0.001', dp: 3 },
  { key: 'charges.energy_charges', label: 'Energy Charges (₹)', width: 'w-28', type: 'number', dp: 2 },
  { key: 'charges.fppas_charges', label: 'FPPAS (₹)', width: 'w-24', type: 'number', dp: 2 },
  { key: 'charges.fixed_charges', label: 'Fixed Charges (₹)', width: 'w-28', type: 'number', dp: 2 },
  { key: 'charges.electricity_duty', label: 'Elec. Duty (₹)', width: 'w-24', type: 'number', dp: 2 },
  { key: 'charges.pf_surcharge_incentive', label: 'PF Adj. (₹)', width: 'w-24', type: 'number', dp: 2 },
  { key: 'charges.tod_rebate_surcharge', label: 'TOD Adj. (₹)', width: 'w-24', type: 'number', dp: 2 },
  { key: 'charges.net_payable', label: 'Net Payable (₹)', width: 'w-28', type: 'number', dp: 2 },
  { key: 'tod_breakdown.off_peak_kwh', label: 'Off-Peak (kWh)', width: 'w-24', type: 'number', dp: 0 },
  { key: 'tod_breakdown.morning_peak_kwh', label: 'AM Peak (kWh)', width: 'w-24', type: 'number', dp: 0 },
  { key: 'tod_breakdown.evening_peak_kwh', label: 'PM Peak (kWh)', width: 'w-24', type: 'number', dp: 0 },
];

const getNested = (obj, path) => path.split('.').reduce((acc, k) => acc && acc[k], obj);
const setNested = (obj, path, val) => {
  const parts = path.split('.');
  const next = { ...obj };
  let cur = next;
  for (let i = 0; i < parts.length - 1; i++) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = val;
  return next;
};

function ValidationBadge({ critical, warnings }) {
  if (!critical && !warnings) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${
        critical ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'
      }`}
      data-testid="bill-validation-badge"
    >
      <AlertTriangle className="w-3 h-3" />
      {critical ? `${critical} critical` : `${warnings} flagged`}
    </span>
  );
}

function CellValidationIcon({ issues }) {
  if (!issues?.length) return null;
  const crit = issues.find((i) => i.severity === 'critical');
  const colour = crit ? 'text-red-500' : 'text-amber-500';
  return (
    <span className={`absolute -top-0.5 -right-0.5 ${colour}`} title={issues.map((i) => i.message).join('\n')}>
      <AlertTriangle className="w-3 h-3" />
    </span>
  );
}

function BillReviewTable({ bills, validation, manualEdits, onChange, onApplyFix, onUndoCell, onRemove, onReextract, disabled, totals }) {
  // Visible rows: drop blank/failed-extraction entries, sort chronologically.
  const visible = sortBillsByMonth(bills)
    .filter((x) => !isBlankBill(x.b))
    .map((x) => ({ ...x, issuesByField: groupIssues(validation?.by_index?.[x.originalIndex]) }));

  return (
    <div className="border border-slate-200 rounded-sm relative" data-testid="bill-review-table">
      <div className="overflow-x-auto">
        <table className="text-xs min-w-full">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr>
              {FIELDS.map((f) => (
                <th
                  key={f.key}
                  className={`text-left px-2 py-2 font-medium text-slate-700 ${f.width} whitespace-nowrap ${
                    f.frozen ? 'sticky left-0 bg-slate-50 z-10 border-r border-slate-200' : ''
                  }`}
                >
                  {f.label}
                </th>
              ))}
              <th className="px-2 py-2 w-16 sticky right-0 bg-slate-50 z-10 border-l border-slate-200" aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map(({ b, originalIndex, issuesByField }) => (
              <tr key={originalIndex} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`bill-row-${originalIndex}`}>
                {FIELDS.map((f) => {
                  const raw = getNested(b, f.key);
                  const val = raw === null || raw === undefined ? '' : raw;
                  const issues = issuesByField[f.key] || [];
                  const hasCritical = issues.some((i) => i.severity === 'critical');
                  const edited = manualEdits?.[originalIndex]?.[f.key];
                  const fix = issues.find((i) => i.suggested_fix)?.suggested_fix;
                  return (
                    <td
                      key={f.key}
                      className={`px-1 py-1 align-top ${
                        f.frozen ? 'sticky left-0 bg-white z-[5] border-r border-slate-200' : ''
                      }`}
                    >
                      <div className="relative">
                        <input
                          type={f.type}
                          step={f.step}
                          value={val}
                          disabled={disabled}
                          onChange={(e) => {
                            const v = f.type === 'number'
                              ? (e.target.value === '' ? null : Number(e.target.value))
                              : e.target.value;
                            onChange(originalIndex, f.key, v);
                          }}
                          data-testid={`bill-${originalIndex}-${f.key.replace(/\./g, '_')}`}
                          className={`w-full px-2 py-1 border rounded-sm text-xs font-mono transition-colors ${
                            hasCritical
                              ? 'border-red-300 bg-red-50 focus:border-red-500'
                              : issues.length
                              ? 'border-amber-300 bg-amber-50 focus:border-amber-500'
                              : edited
                              ? 'border-blue-200 bg-blue-50/40 focus:border-[#0061DD]'
                              : 'border-slate-200 bg-slate-50/40 hover:border-slate-300 focus:border-[#0061DD] focus:bg-white'
                          } focus:outline-none cursor-text`}
                        />
                        <CellValidationIcon issues={issues} />
                        {edited && (
                          <span
                            className="absolute -top-0.5 -left-0.5 w-1.5 h-1.5 rounded-full bg-[#0061DD]"
                            title={`Manually edited (AI extracted: ${edited.original ?? '—'})`}
                            data-testid={`bill-edited-${originalIndex}-${f.key.replace(/\./g, '_')}`}
                          />
                        )}
                      </div>
                      {fix && (
                        <button
                          type="button"
                          onClick={() => onApplyFix(originalIndex, fix)}
                          disabled={disabled}
                          className="mt-1 w-full text-[10px] text-left px-1.5 py-0.5 bg-[#0061DD]/10 text-[#0061DD] border border-[#0061DD]/30 hover:bg-[#0061DD]/20 rounded-sm leading-tight"
                          data-testid={`quick-fix-${originalIndex}-${f.key.replace(/\./g, '_')}`}
                        >
                          ✨ {fix.label}
                        </button>
                      )}
                      {edited && (
                        <button
                          type="button"
                          onClick={() => onUndoCell(originalIndex, f.key)}
                          disabled={disabled}
                          className="mt-0.5 text-[9px] text-slate-400 hover:text-[#0061DD]"
                          data-testid={`undo-cell-${originalIndex}-${f.key.replace(/\./g, '_')}`}
                        >
                          ↶ undo
                        </button>
                      )}
                    </td>
                  );
                })}
                <td className="px-1 py-1 text-right sticky right-0 bg-white z-[5] border-l border-slate-200">
                  {!disabled && (
                    <div className="flex gap-1 justify-end items-center">
                      {b.upload_id && (
                        <button
                          onClick={() => onReextract(originalIndex, b)}
                          title="Re-extract this bill"
                          className="text-slate-400 hover:text-[#0061DD]"
                          data-testid={`reextract-bill-${originalIndex}`}
                        >
                          <RefreshCw className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => onRemove(originalIndex)}
                        className="text-slate-400 hover:text-red-600"
                        data-testid={`remove-bill-${originalIndex}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {totals && visible.length > 1 && (
              <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold" data-testid="bill-totals-row">
                {FIELDS.map((f) => {
                  if (f.key === 'bill_month') {
                    return (
                      <td
                        key={f.key}
                        className="px-2 py-2 sticky left-0 bg-slate-50 z-[5] border-r border-slate-200 text-slate-700"
                      >
                        TOTAL · {visible.length} mo
                      </td>
                    );
                  }
                  const v = totals[f.key];
                  if (v === undefined || v === null) {
                    return <td key={f.key} className="px-2 py-2 text-slate-400">—</td>;
                  }
                  const dp = f.dp ?? 0;
                  const label = f.key === 'power_factor'
                    ? `Wt avg ${v.toFixed(3)}`
                    : f.key === 'max_demand_kw'
                    ? `Max ${v.toFixed(dp)}`
                    : Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });
                  return (
                    <td key={f.key} className="px-2 py-2 text-slate-700 font-mono text-xs whitespace-nowrap">
                      {label}
                    </td>
                  );
                })}
                <td className="sticky right-0 bg-slate-50 z-[5] border-l border-slate-200" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* Right-edge scroll fade indicator */}
      <div className="pointer-events-none absolute top-0 right-0 h-full w-6 bg-gradient-to-l from-white to-transparent" aria-hidden="true" />
    </div>
  );
}

function groupIssues(issues) {
  const out = {};
  for (const i of issues || []) {
    (out[i.field] = out[i.field] || []).push(i);
  }
  return out;
}

function MetricCard({ label, value, sub, tone }) {
  const accent = tone === 'warn'
    ? 'border-t-amber-500'
    : tone === 'crit'
    ? 'border-t-red-500'
    : 'border-t-[#0061DD]';
  const subColor = tone === 'warn'
    ? 'text-amber-700'
    : tone === 'crit'
    ? 'text-red-700'
    : 'text-slate-500';
  return (
    <div className={`bg-white border border-slate-200 border-t-2 ${accent} rounded-sm p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-base font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className={`text-[10px] mt-0.5 ${subColor}`}>{sub}</div>}
    </div>
  );
}

function LiveInsightsPreview({ bills, contractDemandKw, validation }) {
  const live = useMemo(
    () => recomputeHeadlineInsights(bills, contractDemandKw),
    [bills, contractDemandKw],
  );
  if (!live.months) return null;
  const blendedOff = validation?.by_index
    && Object.values(validation.by_index).some((v) => v.some((i) => i.rule === 'blended_rate'));
  const blendedSeverity = blendedOff
    ? (Object.values(validation.by_index).some((v) => v.some((i) => i.rule === 'blended_rate' && i.severity === 'critical')) ? 'crit' : 'warn')
    : null;
  const exceeded = contractDemandKw && live.peak_max_demand_kw > Number(contractDemandKw);
  return (
    <div className="bg-blue-50/30 border border-blue-100 rounded-sm p-4" data-testid="live-insights-preview">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-[#0061DD]"/>Live insights · updates as you edit
        </h3>
        <span className="text-[10px] text-slate-500">{live.months} months</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MetricCard
          label="Total kWh"
          value={live.total_units_kwh.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          sub={`${live.months} months`}
        />
        <MetricCard
          label="Total cost"
          value={formatINRIndian(live.total_cost_inr, { compact: true })}
          sub={`${formatINRIndian(live.avg_monthly_bill_inr, { compact: true })} avg/month`}
        />
        <MetricCard
          label="Blended rate"
          value={`₹${live.blended_rate_inr_per_kwh.toFixed(2)}/kWh`}
          sub={blendedOff ? '⚠️ Expected ₹4-25/kWh — check extraction' : 'Expected ₹4-25/kWh'}
          tone={blendedSeverity}
        />
        <MetricCard
          label="Peak max demand"
          value={`${live.peak_max_demand_kw.toFixed(2)} kW`}
          sub={
            contractDemandKw
              ? (exceeded
                  ? `EXCEEDED · vs contract ${Number(contractDemandKw).toFixed(0)} kW`
                  : `vs contract ${Number(contractDemandKw).toFixed(0)} kW`)
              : '—'
          }
          tone={exceeded ? 'warn' : null}
        />
      </div>
    </div>
  );
}

export default function SiteAnalysisTab({ site }) {
  const siteId = site.prospect_site_id;
  const navigate = window.location;
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState({ electricity_bills: [], dg_log_rows: [], dg_log_files: [] });
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState(null);
  // manualEdits[idx][fieldPath] = { original: <ai_value> } — drives the "dot"
  // change indicator and per-cell undo.
  const [manualEdits, setManualEdits] = useState({});
  const validateTimer = useRef(null);

  const fetchSources = useCallback(async () => {
    try {
      const r = await siteAnalysisAPI.listSources(siteId);
      setSources(r.data);
      // pre-select every vault bill by default
      const ids = new Set((r.data?.electricity_bills || []).map(b => b.upload_id));
      setSelectedBills(ids);
    } catch (e) { /* surfaced via UI empty state */ }
  }, [siteId]);

  const fetchLatest = useCallback(async () => {
    setLoading(true);
    try {
      const list = await siteAnalysisAPI.listForSite(siteId);
      if (list.data && list.data.length) {
        const latest = list.data[0];
        setAnalysis(latest);
        // Kick off validation for the loaded bills
        if (latest.bill_data?.length) {
          try {
            const v = await siteAnalysisAPI.validateBills(latest.analysis_id, latest.bill_data);
            setValidation(v.data);
          } catch (_) { /* advisory only */ }
        }
      } else {
        setAnalysis(null);
      }
    } catch (e) {
      toast.error(e.userMessage || 'Failed to load analyses');
    } finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { fetchLatest(); fetchSources(); }, [fetchLatest, fetchSources]);

  const handleStart = async () => {
    try {
      const r = await siteAnalysisAPI.create(siteId);
      setAnalysis(r.data);
      toast.success('Analysis started');
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Failed to start analysis');
    }
  };

  const toggleBill = (upload_id) => {
    setSelectedBills(prev => {
      const next = new Set(prev);
      if (next.has(upload_id)) next.delete(upload_id); else next.add(upload_id);
      return next;
    });
  };

  const handleExtract = async () => {
    if (!analysis) return;
    if (!selectedBills.size) { toast.error('Select at least one electricity bill from the vault'); return; }
    setExtracting(true);
    try {
      const r = await siteAnalysisAPI.extractFromVault(analysis.analysis_id, [...selectedBills]);
      toast.success(`Extracted ${r.data.bill_count} bills`);
      if (r.data.errors?.length) {
        for (const e of r.data.errors) toast.error(`${e.filename}: ${e.error}`);
      }
      await fetchLatest();
      setManualEdits({});  // fresh extraction → no manual edits yet
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Extraction failed');
    } finally { setExtracting(false); }
  };

  const goToRequirement = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('stage', 'requirement');
    navigate.href = url.toString();
  };

  const triggerValidation = useCallback((bills) => {
    if (!analysis) return;
    if (validateTimer.current) clearTimeout(validateTimer.current);
    validateTimer.current = setTimeout(async () => {
      try {
        const r = await siteAnalysisAPI.validateBills(analysis.analysis_id, bills);
        setValidation(r.data);
      } catch (e) { /* validation is advisory — never block the UI on its failure */ }
    }, 350);
  }, [analysis]);

  const handleRowChange = (idx, fieldPath, value) => {
    setAnalysis(a => {
      const next = { ...a, bill_data: [...(a.bill_data || [])] };
      const original = getNested(next.bill_data[idx] || {}, fieldPath);
      next.bill_data[idx] = setNested(next.bill_data[idx] || {}, fieldPath, value);
      // Remember the AI's original value the FIRST time the user touches a cell.
      setManualEdits(prev => {
        const cellEdited = prev[idx]?.[fieldPath];
        if (cellEdited) return prev;
        return { ...prev, [idx]: { ...(prev[idx] || {}), [fieldPath]: { original } } };
      });
      triggerValidation(next.bill_data);
      return next;
    });
  };

  const handleApplyFix = (idx, fix) => {
    setAnalysis(a => {
      const next = { ...a, bill_data: [...(a.bill_data || [])] };
      const before = next.bill_data[idx];
      const after = applyQuickFix(before, fix);
      next.bill_data[idx] = after;
      // Mark every field the fix touched as manually edited
      setManualEdits(prev => {
        const cur = { ...(prev[idx] || {}) };
        for (const path of fix.fields || []) {
          if (!cur[path]) cur[path] = { original: getNested(before, path) };
        }
        return { ...prev, [idx]: cur };
      });
      triggerValidation(next.bill_data);
      return next;
    });
    toast.success('Quick-fix applied — review the result before saving');
  };

  const handleUndoCell = (idx, fieldPath) => {
    setAnalysis(a => {
      const original = manualEdits[idx]?.[fieldPath]?.original;
      if (original === undefined) return a;
      const next = { ...a, bill_data: [...(a.bill_data || [])] };
      next.bill_data[idx] = setNested(next.bill_data[idx] || {}, fieldPath, original);
      setManualEdits(prev => {
        const cur = { ...(prev[idx] || {}) };
        delete cur[fieldPath];
        const out = { ...prev };
        if (Object.keys(cur).length) out[idx] = cur; else delete out[idx];
        return out;
      });
      triggerValidation(next.bill_data);
      return next;
    });
  };

  const handleReextract = async (idx, bill) => {
    if (!analysis || !bill?.upload_id) {
      toast.error('This bill has no source file — cannot re-extract.');
      return;
    }
    try {
      const r = await siteAnalysisAPI.extractFromVault(analysis.analysis_id, [bill.upload_id]);
      toast.success(`Re-extracted ${bill.original_filename || 'bill'}`);
      if (r.data.errors?.length) {
        for (const e of r.data.errors) toast.error(`${e.filename}: ${e.error}`);
      }
      // Refresh full analysis (re-extract replaces the row with same upload_id)
      await fetchLatest();
      // The row index may shift after refresh — drop manualEdits for this idx
      setManualEdits(prev => { const c = { ...prev }; delete c[idx]; return c; });
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Re-extraction failed');
    }
  };

  const handleRemoveRow = (idx) => {
    setAnalysis(a => ({ ...a, bill_data: a.bill_data.filter((_, i) => i !== idx) }));
  };

  const handleSaveDraft = async () => {
    if (!analysis) return;
    setSaving(true);
    try {
      const r = await siteAnalysisAPI.saveBillData(analysis.analysis_id, analysis.bill_data || []);
      setAnalysis(r.data);
      if (r.data.bill_validation) setValidation(r.data.bill_validation);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    if (!analysis) return;
    if (!analysis.bill_data?.length) { toast.error('Upload at least one bill first'); return; }
    if (validation && !validation.can_generate_report) {
      toast.error(`Cannot analyse — ${validation.critical_count} critical extraction issue(s). Apply the suggested Quick-Fixes first.`);
      return;
    }
    setConfirming(true);
    try {
      await siteAnalysisAPI.saveBillData(analysis.analysis_id, analysis.bill_data);
      const r = await siteAnalysisAPI.confirm(analysis.analysis_id);
      setAnalysis(r.data);
      toast.success('Analysis complete — review the insights below');
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Analysis failed');
    } finally { setConfirming(false); }
  };

  const handleGenerate = async () => {
    if (!analysis) return;
    setGenerating(true);
    try {
      const r = await siteAnalysisAPI.generateReport(analysis.analysis_id);
      setAnalysis(r.data);
      toast.success('Report generated — opening PDF…');
      try {
        await siteAnalysisAPI.openReport(analysis.analysis_id);
      } catch (openErr) {
        toast.error(openErr.userMessage || 'Could not open PDF — try the link below');
      }
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleOpenReport = async () => {
    try {
      await siteAnalysisAPI.openReport(analysis.analysis_id);
    } catch (e) {
      toast.error(e.userMessage || e.response?.data?.detail || 'Could not open report');
    }
  };

  const handleReset = async () => {
    if (!analysis) return;
    if (!window.confirm('Discard this analysis and start fresh? All uploaded bills will be lost.')) return;
    try {
      await siteAnalysisAPI.delete(analysis.analysis_id);
      setAnalysis(null);
      toast.success('Analysis discarded');
    } catch (e) {
      toast.error(e.userMessage || 'Failed to discard');
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Loading analyses…</div>;
  }

  // Empty state — start new analysis
  if (!analysis) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="analysis-empty">
        <FlaskConical className="w-10 h-10 mx-auto text-[#0061DD] mb-3"/>
        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">Energy Advisory Report</h3>
        <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
          Upload 3–6 months of electricity bills for this site. AI will extract the data,
          surface consumption / TOD / demand insights, and generate a McKinsey-style PDF
          advisory you can hand to the customer.
        </p>
        <Button onClick={handleStart} className="bg-[#0061DD] hover:bg-[#0050B5]" data-testid="start-analysis">
          <Plus className="w-4 h-4 mr-1.5"/>Start new analysis
        </Button>
      </div>
    );
  }

  const status = analysis.status;
  const isLocked = status === 'confirmed' || status === 'complete';
  const activeStage = status === 'complete' ? 'report'
    : status === 'confirmed' ? 'report'
    : (analysis.bill_data?.length ? 'review' : 'sources');

  const an = analysis.analysis_results?.analytics;
  const nv = analysis.analysis_results?.narrative;
  const ic = analysis.analysis_results?.industry_context;
  const ctx = analysis.site_ctx || {};
  const billCount = sources.electricity_bills.length;
  const dgRows = sources.dg_log_rows || [];

  return (
    <div className="space-y-4" data-testid="analysis-tab">
      <StageStepper activeStage={activeStage} analysis={analysis}/>

      {/* Pre-populated context from Requirement (read-only summary) */}
      <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="context-summary">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-[#0061DD]"/>From the Requirement tab
          </h3>
          <button onClick={goToRequirement} className="text-xs text-[#0061DD] hover:underline flex items-center gap-1" data-testid="edit-requirement-link">
            <ExternalLink className="w-3 h-3"/>Edit requirement
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div><span className="text-slate-500">Critical load:</span> <strong>{ctx.critical_load_kw ? `${ctx.critical_load_kw} kW` : '—'}</strong></div>
          <div><span className="text-slate-500">Backup hours:</span> <strong>{ctx.backup_hours ?? '—'} h</strong></div>
          <div><span className="text-slate-500">Site type:</span> <strong>{ctx.site_type || '—'}</strong></div>
          <div><span className="text-slate-500">Use cases:</span> <strong>{(ctx.use_case_flags || []).join(', ') || ctx.use_case || '—'}</strong></div>
          <div><span className="text-slate-500">Has DG:</span> <strong>{ctx.has_dg ? `Yes${ctx.dg_capacity_kva ? ` (${ctx.dg_capacity_kva} kVA)` : ''}` : 'No'}</strong></div>
          <div><span className="text-slate-500">Has solar:</span> <strong>{ctx.has_solar ? `Yes${ctx.solar_pv_kwp ? ` (${ctx.solar_pv_kwp} kWp)` : ''}` : 'No'}</strong></div>
          <div><span className="text-slate-500">City / state:</span> <strong>{[ctx.city, ctx.state].filter(Boolean).join(', ') || '—'}</strong></div>
          <div><span className="text-slate-500">DISCOM:</span> <strong>{ctx.discom_name || 'Auto-detect'}</strong></div>
        </div>
      </div>

      {/* Stage 1: data sources from Document Vault */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Data sources (from Document Vault)</h3>
            <p className="text-xs text-slate-500 mt-0.5">Pulled from the Requirement tab — toggle which bills to include.</p>
          </div>
          {!isLocked && (
            <Button size="sm" onClick={handleExtract} disabled={extracting || !selectedBills.size}
              className="bg-[#0061DD] hover:bg-[#0050B5]" data-testid="extract-from-vault-btn">
              {extracting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Sparkles className="w-4 h-4 mr-1.5"/>}
              Extract &amp; analyse
            </Button>
          )}
        </div>

        {/* Electricity bills list */}
        <div className="border border-slate-200 rounded-sm p-3 mb-2">
          <div className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1.5">
            {billCount > 0 ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600"/> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500"/>}
            Electricity bills: {billCount} found
          </div>
          {billCount === 0 ? (
            <div className="text-xs text-slate-500 italic pl-1">
              No bills in vault. <button onClick={goToRequirement} className="text-[#0061DD] hover:underline">Upload via Requirement tab</button>
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="vault-bill-list">
              {sources.electricity_bills.map(b => (
                <li key={b.upload_id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={selectedBills.has(b.upload_id)}
                    disabled={isLocked || extracting}
                    onChange={() => toggleBill(b.upload_id)}
                    data-testid={`vault-bill-${b.upload_id}`}
                    className="rounded"
                  />
                  <span className="font-mono truncate flex-1">{b.original_filename}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.parsing_status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {b.parsing_status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* DG logs */}
        <div className="border border-slate-200 rounded-sm p-3">
          <div className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1.5">
            {dgRows.length > 0 ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600"/> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500"/>}
            DG logs: {dgRows.length} rows from {sources.dg_log_files.length} file(s)
          </div>
          {dgRows.length === 0 ? (
            <div className="text-xs text-slate-500 italic pl-1">
              No DG logs digitised. <button onClick={goToRequirement} className="text-[#0061DD] hover:underline">Add via Requirement tab</button> if applicable.
            </div>
          ) : (
            <div className="text-xs text-slate-600 pl-1">
              {dgRows.length} digitised entries · auto-included in DG Replacement Economics in the report.
            </div>
          )}
        </div>

        <div className="flex items-center text-xs text-slate-600 mt-3 pt-2 border-t border-slate-100">
          <span className="bg-slate-100 rounded px-2 py-0.5 mr-2 font-mono">{analysis.bill_data?.length || 0}</span>
          <span>bills extracted · status <strong className="text-[#0061DD]">{status}</strong></span>
          <button onClick={handleReset} className="ml-auto text-xs text-slate-400 hover:text-red-600 flex items-center gap-1" data-testid="reset-analysis">
            <RefreshCw className="w-3 h-3"/>Discard
          </button>
        </div>
      </div>

      {/* Industry & site context — drives the new Section 3 of the report */}
      <SiteContextEditor analysis={analysis} onUpdate={setAnalysis}/>

      {/* Stage 2: review */}
      {(analysis.bill_data?.length > 0) && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-slate-900">2. Review &amp; correct extracted data</h3>
              <ValidationBadge critical={validation?.critical_count} warnings={validation?.warning_count} />
            </div>
            {!isLocked && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-bill-draft">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : null}
                  Save draft
                </Button>
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={confirming || (validation && !validation.can_generate_report)}
                  title={validation && !validation.can_generate_report ? `Resolve ${validation.critical_count} critical issue(s) first` : undefined}
                  className="bg-[#0061DD] hover:bg-[#0050B5] disabled:opacity-50"
                  data-testid="confirm-analysis"
                >
                  {confirming ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5 mr-1.5"/>}
                  Confirm &amp; analyse
                </Button>
              </div>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Each row is one month. Cells with a blue dot = manually edited. Red = critical issue. Amber = warning. Hover any cell for details.
          </p>
          {validation?.total_issues > 0 && (
            <div className={`mb-3 px-3 py-2 rounded-sm text-xs border ${
              validation.critical_count
                ? 'bg-red-50 border-red-200 text-red-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
            }`} data-testid="validation-summary-banner">
              <strong>
                {validation.critical_count > 0
                  ? `⚠️ ${validation.critical_count} critical issue${validation.critical_count > 1 ? 's' : ''} — `
                  : `${validation.warning_count} value${validation.warning_count > 1 ? 's' : ''} flagged — `}
              </strong>
              {validation.critical_count > 0
                ? 'cannot run analysis until resolved. Apply the suggested Quick-Fixes or correct manually.'
                : 'hover over highlighted cells to see details. Quick-Fix buttons appear under common decimal errors.'}
            </div>
          )}
          <BillReviewTable
            bills={analysis.bill_data}
            validation={validation}
            manualEdits={manualEdits}
            onChange={handleRowChange}
            onApplyFix={handleApplyFix}
            onUndoCell={handleUndoCell}
            onRemove={handleRemoveRow}
            onReextract={handleReextract}
            disabled={isLocked}
            totals={computeBillTotals(analysis.bill_data)}
          />
        </div>
      )}

      {/* Live insights preview — recomputed on every cell edit (before confirm) */}
      {(analysis.bill_data?.length > 0) && !isLocked && (
        <LiveInsightsPreview
          bills={analysis.bill_data}
          contractDemandKw={ctx.contract_demand_kw}
          validation={validation}
        />
      )}

      {/* Stage 3: results + report */}
      {an && (
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="analysis-results">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">3. Insights & report</h3>
              <p className="text-xs text-slate-500 mt-0.5">Generated narrative + PDF download.</p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleGenerate}
                disabled={generating || (validation && !validation.can_generate_report)}
                title={validation && !validation.can_generate_report ? `Resolve ${validation.critical_count} critical issue(s) first` : undefined}
                className="bg-[#0061DD] hover:bg-[#0050B5] disabled:opacity-50"
                data-testid="generate-report-btn"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5 mr-1.5"/>}
                Generate report
              </Button>
              {status === 'complete' && (
                <button
                  type="button"
                  onClick={handleOpenReport}
                  className="inline-flex items-center px-3 py-1.5 text-xs border border-slate-200 rounded-sm hover:bg-slate-50"
                  data-testid="download-report-link"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5"/>Open PDF
                </button>
              )}
            </div>
          </div>

          {/* Metric cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <MetricCard label="Total kWh" value={formatKWh(an.consumption_cost?.total_units_kwh)} sub={`${an.period?.months || 0} months`}/>
            <MetricCard
              label="Total cost"
              value={formatINR(an.consumption_cost?.total_cost_inr, { compact: true })}
              sub={`${formatINRIndian(an.consumption_cost?.avg_monthly_bill_inr, { compact: true })} avg/month`}
            />
            <MetricCard
              label="Blended rate"
              value={`₹${(an.consumption_cost?.blended_rate_inr_per_kwh || 0).toFixed(2)}/kWh`}
              sub={validation?.by_index && Object.values(validation.by_index).some(v => v.some(i => i.rule === 'blended_rate')) ? '⚠️ Expected ₹4-25/kWh' : 'Expected ₹4-25/kWh'}
              tone={validation?.by_index && Object.values(validation.by_index).some(v => v.some(i => i.rule === 'blended_rate')) ? 'warn' : null}
            />
            <MetricCard label="Avg monthly bill" value={formatINR(an.consumption_cost?.avg_monthly_bill_inr)}/>
            <MetricCard
              label="Peak max demand"
              value={`${(an.max_demand?.peak_observed_kw || 0).toFixed(2)} kW`}
              sub={
                an.max_demand?.contract_demand_kw
                  ? (an.max_demand.peak_observed_kw > an.max_demand.contract_demand_kw
                      ? `EXCEEDED · vs contract ${an.max_demand.contract_demand_kw.toFixed(0)} kW`
                      : `vs contract ${an.max_demand.contract_demand_kw.toFixed(0)} kW`)
                  : '—'
              }
              tone={an.max_demand?.contract_demand_kw && an.max_demand.peak_observed_kw > an.max_demand.contract_demand_kw ? 'warn' : null}
            />
            <MetricCard label="Latest PF" value={`${(an.power_factor?.latest || 0).toFixed(2)} · ${an.power_factor?.status || '—'}`}/>
            <MetricCard label="Evening peak share" value={`${(an.tod_analysis?.evening_peak_share_pct || 0).toFixed(1)}%`}/>
            <MetricCard label="BESS sizing" value={`${(an.bess_sizing?.min_capacity_kwh || 0).toLocaleString('en-IN')} kWh`} sub={`${(an.bess_sizing?.recommended_pcs_kw || 0).toFixed(0)} kW PCS`}/>
          </div>

          {/* Narrative */}
          {nv?.executive_summary_bullets?.length > 0 && (
            <div className="mt-4 bg-slate-50 border border-slate-200 rounded-sm p-4">
              <div className="text-xs font-semibold text-[#0061DD] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5"/> Key findings
              </div>
              <ul className="text-sm text-slate-700 space-y-2 list-disc pl-5" data-testid="finding-bullets">
                {nv.executive_summary_bullets.map((b, i) => <li key={b || i}>{b}</li>)}
              </ul>
              {nv.bottom_line && (
                <div className="mt-3 pt-3 border-t border-slate-200 text-sm font-medium text-slate-900">
                  <span className="text-[#0061DD] mr-1.5">Bottom line —</span>{nv.bottom_line}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
